import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useMutation, useQueryClient } from 'react-query';
import { Typography, Grid, InputAdornment, Divider, Box } from '@mui/material';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Paper } from 'shared/ui/Paper';
import { FormSwitch } from 'shared/ui/Switch/FormSwitch';
import { Button } from 'shared/ui/Button';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { InputField } from 'shared/ui/InputField';
import { useModelsOptions } from 'shared/hooks/use-models-options';
import { postInboundEmails } from 'entities/inbound-emails/api';
import { CategoryTypeEnum } from 'entities/Files/types';

const AddInboundEmailSchema = yup.object({
	category: yup.string().test('category', 'shared_texts.field_is_required', function (value) {
		return !value ? false : true;
	}),
	imgMinResolution: yup.number(),
	isHandleEmailBody: yup.bool(),
	isExtractOriginalEmail: yup.bool(),
});

type AddInboundEmailFormType = yup.InferType<typeof AddInboundEmailSchema>;

const AdditionForm: FC<{ closeForm: () => void }> = ({ closeForm }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { modelsOptions, getModelType } = useModelsOptions();

	const { mutate } = useMutation({
		mutationKey: ['post/inbound-emails'],
		mutationFn: postInboundEmails,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['get/inbound-emails'] });
			enqueueSnackbar({
				message: t('extensions.snackbar.success_inbound_email_creation'),
				variant: 'success',
			});
			closeForm();
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const handleSubmit = (values: AddInboundEmailFormType) => {
		const category = (getModelType(values.category || '') || values.category) as CategoryTypeEnum;
		mutate({
			category,
			customModelId:
				getModelType(values.category || '') === CategoryTypeEnum.custom ? values.category : undefined,
			generativeModelId:
				getModelType(values.category || '') === CategoryTypeEnum.generative_model
					? values.category
					: undefined,
			imgMinResolution: values.imgMinResolution,
			isHandleEmailBody: values.isHandleEmailBody,
			isExtractOriginalEmail: values.isExtractOriginalEmail,
		});
	};

	return (
		<Box
			sx={{
				'width': '100%',
				'@media screen and (min-width: 1441px)': {
					width: '65%',
				},
			}}
		>
			<Formik
				initialValues={{
					category: undefined,
					imgMinResolution: 1000,
					isHandleEmailBody: false,
					isExtractOriginalEmail: false,
				}}
				validationSchema={AddInboundEmailSchema}
				onSubmit={handleSubmit}
			>
				{({ values, setFieldValue }) => (
					<Form>
						<Grid container direction="column" spacing={5}>
							<Paper
								sx={{
									display: 'flex',
									flexDirection: 'column',
									padding: '30px',
									gap: 3,
									width: '100%',
								}}
							>
								<Grid container spacing={5} alignItems="center">
									<Grid size={7}>
										<Typography variant={'body2'} color={ColorPalette.astronaut}>
											{t('extensions.ex_list.inbound_email.choose_model')}
										</Typography>
									</Grid>
									<Grid size={5}>
										<FormSelect
											name={'category'}
											label={''}
											placeholder={t('partners.placeholder.model_type')}
											options={modelsOptions}
											value={values.category}
											onSelect={(value) => setFieldValue('category', value)}
											required
											sx={{ marginBottom: '0' }}
										/>
									</Grid>
								</Grid>
								<Divider />
								<Grid container spacing={5} alignItems="center">
									<Grid size={7}>
										<Typography variant={'body2'} color={ColorPalette.astronaut}>
											{t('extensions.ex_list.inbound_email.image_height')}
										</Typography>
									</Grid>
									<Grid size={5} container justifyContent="flex-end">
										<InputField
											name={'imgMinResolution'}
											type="number"
											label={''}
											placeholder={t('shared_texts.enter_number')}
											endAdornment={
												<InputAdornment position="end">
													<Typography sx={{ color: ColorPalette.baliHai }}>
														px
													</Typography>
												</InputAdornment>
											}
											sx={{
												width: '200px',
												marginBottom: '0',
											}}
										/>
									</Grid>
								</Grid>
								<Divider />
								<Grid>
									<Grid container spacing={5} alignItems="center">
										<Grid size={7}>
											<Typography variant={'body2'} color={ColorPalette.astronaut}>
												{t('extensions.ex_list.inbound_email.extract_email')}
											</Typography>
										</Grid>
										<Grid size={5} textAlign="right">
											<FormSwitch
												name="isHandleEmailBody"
												sx={{ marginRight: '10px' }}
											/>
										</Grid>
									</Grid>

									{values.isHandleEmailBody && (
										<Grid
											container
											spacing={5}
											alignItems="center"
											sx={{
												paddingLeft: 2.5,
												borderLeft: `1px solid ${ColorPalette.zircon}`,
												marginTop: 3,
											}}
										>
											<Grid size={7}>
												<Typography variant={'body2'} color={ColorPalette.baliHai}>
													{t(
														'extensions.ex_list.inbound_email.extract_original_email',
													)}
												</Typography>
											</Grid>
											<Grid size={5} textAlign="right">
												<FormSwitch
													name="isExtractOriginalEmail"
													sx={{ marginRight: '10px' }}
												/>
											</Grid>
										</Grid>
									)}
								</Grid>
							</Paper>

							<Button type={'submit'} size="small" sx={{ alignSelf: 'flex-end' }}>
								{t('shared_texts.add')}
							</Button>
						</Grid>
					</Form>
				)}
			</Formik>
		</Box>
	);
};

export default AdditionForm;
